import { useState } from "react";
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  Modal,
  Linking,
} from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useLocalSearchParams, useRouter } from "expo-router";
import { NavigateIcon, PencilIcon, CloseCircleIcon, TrashIcon } from "@/components/Icons";
import {
  doc,
  onSnapshot,
  collection,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useEffect } from "react";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useAvailability } from "@/hooks/useAvailability";
import { useMembers } from "@/hooks/useMembers";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { MatchDetailSkeleton } from "@/components/Skeleton";
import { useAlert } from "@/hooks/useAlert";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";
import { setAvailability, withdrawFromMatch, createPost } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { ModalContent } from "@/components/ContentWidth";
import AlertSheet from "@/components/AlertSheet";
import type {
  Match,
  AvailabilityStatus,
  Selection,
  WithdrawalReason,
} from "@/lib/types";

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatTime(timeStr: string): string {
  try {
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m}pm`;
    return `${hour === 0 ? 12 : hour}:${m}am`;
  } catch {
    return timeStr;
  }
}

const WITHDRAWAL_REASONS: WithdrawalReason[] = [
  "Injury",
  "Work",
  "Personal",
  "Other",
];

const statusConfig: Record<
  AvailabilityStatus,
  { label: string; emoji: string; bg: string; border: string; text: string }
> = {
  available: {
    label: "Available",
    emoji: "\u2705",
    bg: colors.brand50,
    border: colors.brandLight,
    text: colors.brandDark,
  },
  unavailable: {
    label: "Unavailable",
    emoji: "\u274C",
    bg: colors.dangerLight,
    border: "#fecaca",
    text: colors.danger,
  },
  maybe: {
    label: "Maybe",
    emoji: "\ud83e\udd14",
    bg: colors.amber50,
    border: colors.amber200,
    text: colors.amber,
  },
};

/* ── Styled Components ── */

/* Screen styled.ScrollView replaced by KeyboardAwareScroll */

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const EmptySection = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  padding: 12px 0;
`;

const MatchHeader = styled.View`
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 20px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const TeamBadge = styled.View`
  background-color: ${colors.bg};
  border-radius: ${radius.sm}px;
  padding-horizontal: 10px;
  padding-vertical: 4px;
  border-width: 1px;
  border-color: ${colors.line};
  align-self: flex-start;
  margin-bottom: 8px;
`;

const TeamBadgeText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkSecondary};
`;

const Opponent = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
`;

const MatchMeta = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  font-weight: ${font.medium};
  margin-top: 4px;
`;

const Section = styled.View`
  padding-horizontal: 20px;
  margin-top: 20px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const MyStatusCard = styled.View<{ bg: string; borderClr: string }>`
  flex-direction: row;
  align-items: center;
  gap: 12px;
  border-radius: ${radius.md}px;
  border-width: 1px;
  padding: 14px;
  margin-bottom: 12px;
  background-color: ${(p) => p.bg};
  border-color: ${(p) => p.borderClr};
`;

const MyStatusText = styled.Text<{ color: string }>`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${(p) => p.color};
`;

const MyStatusNote = styled.Text`
  font-size: 13px;
  color: ${colors.inkSecondary};
  margin-top: 2px;
`;

const ChangeText = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.brand};
`;

const AvailBtns = styled.View`
  flex-direction: row;
  gap: 10px;
`;

const AvailBtn = styled.View<{
  isSelected: boolean;
  bg: string;
  borderClr: string;
}>`
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 14px 8px;
  border-radius: ${radius.md}px;
  border-width: ${(p) => (p.isSelected ? "2px" : "1px")};
  background-color: ${(p) => (p.isSelected ? p.bg : colors.white)};
  border-color: ${(p) => (p.isSelected ? p.borderClr : colors.line)};
`;

const AvailBtnEmoji = styled.Text`
  font-size: 22px;
`;

const AvailBtnLabel = styled.Text<{ color: string }>`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.color};
  margin-top: 4px;
`;

const NoteInput = styled.TextInput`
  background-color: ${colors.white};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  font-size: 14px;
  color: ${colors.ink};
  margin-top: 16px;
  min-height: 48px;
`;

const SummaryRow = styled.View`
  flex-direction: row;
  gap: 8px;
`;

const SummaryPill = styled.View<{ bg: string }>`
  flex: 1;
  border-radius: ${radius.md}px;
  padding-vertical: 12px;
  align-items: center;
  background-color: ${(p) => p.bg};
`;

const SummaryText = styled.Text<{ color: string }>`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${(p) => p.color};
`;

const ResponseCard = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const ResponseRow = styled.View<{ hasBorder: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  border-bottom-width: ${(p) => (p.hasBorder ? 1 : 0)}px;
  border-bottom-color: ${colors.line};
`;

const ResponseAvatar = styled.View<{ bg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  background-color: ${(p) => p.bg};
`;

const ResponseAvatarText = styled.Text<{ color: string }>`
  font-size: 13px;
  font-weight: ${font.extrabold};
  color: ${(p) => p.color};
`;

const ResponseName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const ResponseNote = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const EmptyCard = styled.View`
  margin-horizontal: 20px;
  margin-top: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 24px;
  align-items: center;
`;

const EmojiText = styled.Text`
  font-size: 20px;
`;

const Flex1 = styled.View`
  flex: 1;
`;

const StatusEmoji = styled.Text<{ color: string }>`
  color: ${(p) => p.color};
`;

const SelectTeamBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding: 14px;
  align-items: center;
`;

const SelectTeamBtnText = styled.Text`
  color: #fff;
  font-size: 15px;
  font-weight: ${font.bold};
`;

const ConfirmedBadge = styled.View`
  background-color: ${colors.brand50};
  border-radius: ${radius.md}px;
  border: 1px solid ${colors.brandLight};
  padding: 12px;
  align-items: center;
`;

const ConfirmedBadgeText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.brandDark};
`;

const PayFeeBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding: 14px;
  align-items: center;
`;

const PayFeeBtnText = styled.Text`
  color: #fff;
  font-size: 15px;
  font-weight: ${font.bold};
`;

const PaidBadge = styled.View`
  background-color: ${colors.brand50};
  border-radius: ${radius.md}px;
  border: 1px solid ${colors.brandLight};
  padding: 10px;
  align-items: center;
  margin-top: 12px;
`;

const PaidBadgeText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.brandDark};
`;

const BottomSpacer = styled.View`
  height: 32px;
`;

/* ── Withdrawal styled components ── */

const WithdrawLink = styled.Text`
  font-size: 13px;
  font-weight: ${font.medium};
  color: ${colors.inkMuted};
  text-align: center;
  margin-top: 12px;
  text-decoration-line: underline;
`;

const WithdrawnBadge = styled.View`
  background-color: ${colors.dangerLight};
  border-radius: ${radius.md}px;
  border-width: 1px;
  border-color: #fecaca;
  padding: 12px;
  align-items: center;
  margin-top: 12px;
`;

const WithdrawnBadgeText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.danger};
`;

const WithdrawalCountBadge = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.md}px;
  border-width: 1px;
  border-color: ${colors.amber200};
  padding: 10px;
  align-items: center;
  margin-top: 8px;
`;

const WithdrawalCountText = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const WithdrawSheet = styled.View`
  background-color: ${colors.white};
  border-top-left-radius: ${radius.xl}px;
  border-top-right-radius: ${radius.xl}px;
  padding: 24px;
  padding-bottom: 40px;
`;

const WithdrawSheetTitle = styled.Text`
  font-size: 18px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  text-align: center;
  margin-bottom: 6px;
`;

const WithdrawSheetMessage = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  text-align: center;
  line-height: 20px;
  margin-bottom: 20px;
`;

const ReasonPillsRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
`;

const ReasonPill = styled.View<{ isActive: boolean }>`
  padding-horizontal: 16px;
  padding-vertical: 10px;
  border-radius: ${radius.full}px;
  border-width: 2px;
  border-color: ${(p) => (p.isActive ? colors.danger : colors.line)};
  background-color: ${(p) => (p.isActive ? colors.dangerLight : colors.white)};
`;

const ReasonPillText = styled.Text<{ isActive: boolean }>`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${(p) => (p.isActive ? colors.danger : colors.inkSecondary)};
`;

const WithdrawConfirmBtn = styled.View<{ disabled: boolean }>`
  background-color: ${(p) => (p.disabled ? colors.lineHover : colors.danger)};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
`;

const WithdrawConfirmBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const WithdrawCancelBtn = styled.View`
  padding: 14px;
  align-items: center;
  margin-top: 4px;
`;

const WithdrawCancelBtnText = styled.Text`
  color: ${colors.inkMuted};
  font-size: 15px;
  font-weight: ${font.bold};
`;

/* ── Component ── */

export default function MatchDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const [match, setMatch] = useState<(Match & { id: string }) | null>(null);
  const [matchLoading, setMatchLoading] = useState(true);
  const {
    responses,
    available,
    unavailable,
    maybe,
    myResponse,
    loading: availLoading,
  } = useAvailability(club?.id, id);
  const { members } = useMembers(club?.id);
  const [note, setNote] = useState("");
  const [availExpanded, setAvailExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myFee, setMyFee] = useState<{ amount: number; paid: boolean } | null>(
    null,
  );

  // Withdrawal state
  const [mySelection, setMySelection] = useState<Selection | null>(null);
  const [withdrawalCount, setWithdrawalCount] = useState(0);
  const [withdrawSheetVisible, setWithdrawSheetVisible] = useState(false);
  const [selectedReason, setSelectedReason] = useState<WithdrawalReason | null>(
    null,
  );
  const [withdrawing, setWithdrawing] = useState(false);
  const { alertProps, alert } = useAlert();
  const { confirmProps, confirm } = useConfirm();

  const { isCaptainOf } = useTeamConfig(club?.id);

  // Listen to my selection for this match
  useEffect(() => {
    if (!club?.id || !id || !user?.uid) return;
    const unsub = onSnapshot(
      doc(db, "clubs", club.id, "matches", id, "selections", user.uid),
      (snap) => {
        if (snap.exists()) {
          setMySelection(snap.data() as Selection);
        } else {
          setMySelection(null);
        }
      },
    );
    return unsub;
  }, [club?.id, id, user?.uid]);

  // Listen to all selections to count withdrawals
  useEffect(() => {
    if (!club?.id || !id) return;
    const unsub = onSnapshot(
      collection(db, "clubs", club.id, "matches", id, "selections"),
      (snap) => {
        let wCount = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          if (data.selected && data.withdrawn) wCount++;
        });
        setWithdrawalCount(wCount);
      },
    );
    return unsub;
  }, [club?.id, id]);

  // Listen to my fee for this match
  useEffect(() => {
    if (!club?.id || !id || !user?.uid) return;
    const unsub = onSnapshot(
      doc(db, "clubs", club.id, "matches", id, "fees", user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setMyFee({ amount: data.amount, paid: data.paid });
        } else {
          setMyFee(null);
        }
      },
    );
    return unsub;
  }, [club?.id, id, user?.uid]);
  const myStatus = user ? myResponse(user.uid) : undefined;
  const canSelect =
    user &&
    match &&
    (isCaptainOf(match.team, user.uid) || membership?.role === "admin");
  const canEdit = membership?.role === "admin";

  const handleDeleteFixture = async () => {
    if (!club || !id) return;
    const ok = await confirm({
      title: "Delete fixture",
      message:
        "This fixture and all its data (availability, fees, selections) will be permanently deleted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await deleteDoc(doc(db, "clubs", club.id, "matches", id));
    router.back();
  };

  const handleCancelFixture = async () => {
    if (!club || !id) return;
    const ok = await confirm({
      title: "Cancel fixture",
      message: `Mark ${match?.team} vs ${match?.opponent} as cancelled?`,
      confirmLabel: "Cancel fixture",
      destructive: true,
    });
    if (!ok) return;
    await updateDoc(doc(db, "clubs", club.id, "matches", id), {
      status: "cancelled",
    });
    await alert({
      title: "Fixture cancelled",
      emoji: "🚫",
      message: "This fixture has been cancelled.",
    });
  };

  const isSelected = mySelection?.selected === true && !mySelection?.withdrawn;
  const hasWithdrawn =
    mySelection?.selected === true && mySelection?.withdrawn === true;

  // Group all members by their response status
  const respondedIds = new Set(responses.map((r) => r.userId));
  const notResponded = members.filter((m) => !respondedIds.has(m.userId));

  useEffect(() => {
    if (!club?.id || !id) return;
    const unsub = onSnapshot(
      doc(db, "clubs", club.id, "matches", id),
      (snap) => {
        if (snap.exists()) {
          setMatch({ id: snap.id, ...snap.data() } as Match & { id: string });
        }
        setMatchLoading(false);
      },
    );
    return unsub;
  }, [club?.id, id]);

  const handleSetAvailability = async (status: AvailabilityStatus) => {
    if (!club || !user || !membership || !match || !id) return;
    setSaving(true);
    await setAvailability(club.id, id, {
      userId: user.uid,
      displayName: membership.displayName,
      status,
      note: note.trim(),
      clubId: club.id,
      clubName: club.name,
      matchDate: match.date,
      team: match.team,
    });
    setSaving(false);
  };

  const handleWithdraw = async () => {
    if (!club || !user || !membership || !match || !id || !selectedReason)
      return;
    setWithdrawing(true);
    try {
      await withdrawFromMatch(club.id, id, user.uid, selectedReason);

      // Create feed post
      await createPost(club.id, {
        authorId: user.uid,
        authorName: membership.displayName,
        authorRole: membership.role,
        text: `\u26A0\uFE0F ${membership.displayName} has withdrawn from ${match.team} vs ${match.opponent} \u2014 Reason: ${selectedReason}`,
        visibility: "internal",
        clubName: club.name,
      });

      setWithdrawSheetVisible(false);
      setSelectedReason(null);

      await alert({
        title: "You've withdrawn",
        emoji: "\u26A0\uFE0F",
        message: "The captain has been notified.",
      });
    } catch (err: any) {
      await alert({
        title: "Error",
        emoji: "\ud83d\ude15",
        message: err.message || "Failed to withdraw.",
        buttonColor: colors.danger,
      });
    } finally {
      setWithdrawing(false);
    }
  };

  if (matchLoading || availLoading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <MatchDetailSkeleton />
      </View>
    );
  }

  if (!match) {
    return (
      <Center>
        <EmptyText>Match not found.</EmptyText>
      </Center>
    );
  }

  const isTeamSelected = match.status === "selected";

  return (
    <KeyboardAwareScroll
      style={{ flex: 1, backgroundColor: colors.bg }}
      showsVerticalScrollIndicator={false}
    >
      <ContentWidth>
        {/* Match header */}
        <MatchHeader>
          <TeamBadge>
            <TeamBadgeText>{match.team}</TeamBadgeText>
          </TeamBadge>
          <Opponent>vs {match.opponent}</Opponent>
          <MatchMeta>
            {formatDate(match.date)} · {formatTime(match.time)}
          </MatchMeta>
          <MatchMeta>
            {match.venue.charAt(0).toUpperCase() + match.venue.slice(1)} ·{" "}
            {match.type.charAt(0).toUpperCase() + match.type.slice(1)}
          </MatchMeta>
          {match.location && (
            <MatchMeta style={{ marginTop: 4 }}>{match.location}</MatchMeta>
          )}
          <View style={{ flexDirection: "row", gap: 10, marginTop: 12 }}>
            {match.location && (
              <Pressable
                onPress={() =>
                  Linking.openURL(
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(match.location!)}`,
                  )
                }
                style={({ pressed }) => ({
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: colors.brand50,
                  borderWidth: 1,
                  borderColor: "#86efac",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <NavigateIcon size={18} color={colors.brandDark} />
              </Pressable>
            )}
            {canEdit && match && (
              <>
                <Pressable
                  onPress={() =>
                    router.push({
                      pathname: "/(app)/create-fixture",
                      params: {
                        matchId: id,
                        team: match.team,
                        opponent: match.opponent,
                        date: match.date,
                        time: match.time,
                        venue: match.venue,
                        type: match.type,
                        location: match.location || "",
                      },
                    })
                  }
                  style={({ pressed }) => ({
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: colors.brand50,
                    borderWidth: 1,
                    borderColor: "#86efac",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <PencilIcon size={18} color={colors.brandDark} />
                </Pressable>
                <Pressable
                  onPress={handleCancelFixture}
                  style={({ pressed }) => ({
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: colors.amber50,
                    borderWidth: 1,
                    borderColor: colors.amber200,
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <CloseCircleIcon size={18} color={colors.amber} />
                </Pressable>
                <Pressable
                  onPress={handleDeleteFixture}
                  style={({ pressed }) => ({
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    backgroundColor: colors.dangerLight,
                    borderWidth: 1,
                    borderColor: "#fecaca",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <TrashIcon size={18} color={colors.danger} />
                </Pressable>
              </>
            )}
          </View>
        </MatchHeader>

        {/* Select team button (captain/admin only) */}
        {canSelect &&
          match.status !== "selected" &&
          match.status !== "completed" &&
          (!match.availabilityOpensAt ||
            match.availabilityOpensAt <=
              new Date().toISOString().slice(0, 10)) && (
            <Section>
              <Pressable
                onPress={() =>
                  router.push(`/(app)/team-selection?matchId=${id}`)
                }
              >
                <SelectTeamBtn>
                  <SelectTeamBtnText>Select {match.team} →</SelectTeamBtnText>
                </SelectTeamBtn>
              </Pressable>
            </Section>
          )}

        {/* Captain can still edit team after selection if there are withdrawals */}
        {canSelect && isTeamSelected && (
          <Section>
            <Pressable
              onPress={() => router.push(`/(app)/team-selection?matchId=${id}`)}
            >
              <SelectTeamBtn style={{ backgroundColor: colors.amber }}>
                <SelectTeamBtnText>Edit Team Selection →</SelectTeamBtnText>
              </SelectTeamBtn>
            </Pressable>
          </Section>
        )}

        {isTeamSelected && (
          <Section>
            <ConfirmedBadge>
              <ConfirmedBadgeText>{"\u2705"} Team confirmed</ConfirmedBadgeText>
            </ConfirmedBadge>

            {/* Withdrawal count */}
            {withdrawalCount > 0 && (
              <WithdrawalCountBadge>
                <WithdrawalCountText>
                  {"\u26A0\uFE0F"} {withdrawalCount} withdrawal
                  {withdrawalCount > 1 ? "s" : ""}
                </WithdrawalCountText>
              </WithdrawalCountBadge>
            )}

            {/* Player has withdrawn */}
            {hasWithdrawn && (
              <WithdrawnBadge>
                <WithdrawnBadgeText>
                  You've withdrawn from this match
                </WithdrawnBadgeText>
              </WithdrawnBadge>
            )}

            {/* Player is selected and can withdraw */}
            {isSelected && !hasWithdrawn && (
              <Pressable onPress={() => setWithdrawSheetVisible(true)}>
                <WithdrawLink>Can't play anymore?</WithdrawLink>
              </Pressable>
            )}

            {myFee && !myFee.paid && !hasWithdrawn && !membership?.isJunior && (
              <Pressable
                style={{ marginTop: 12 }}
                onPress={() =>
                  router.push(
                    `/(app)/pay?amount=${myFee.amount}&description=${encodeURIComponent(`Match fee - ${match.team} vs ${match.opponent}`)}&matchId=${id}&feeId=${user?.uid}&feeType=match_fee`,
                  )
                }
              >
                <PayFeeBtn>
                  <PayFeeBtnText>
                    Pay £{(myFee.amount / 100).toFixed(2)} match fee
                  </PayFeeBtnText>
                </PayFeeBtn>
              </Pressable>
            )}
            {myFee?.paid && (
              <PaidBadge>
                <PaidBadgeText>{"\u2705"} Match fee paid</PaidBadgeText>
              </PaidBadge>
            )}
          </Section>
        )}

        {/* Availability not yet open — show notice + admin "open now" */}
        {match &&
          match.availabilityOpensAt &&
          match.availabilityOpensAt > new Date().toISOString().slice(0, 10) && (
            <Section>
              <SectionLabel>Availability</SectionLabel>
              <View
                style={{
                  padding: 16,
                  backgroundColor: colors.bg,
                  borderRadius: radius.md,
                  borderWidth: 1,
                  borderColor: colors.line,
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    color: colors.inkMuted,
                    textAlign: "center",
                    fontWeight: "600",
                  }}
                >
                  Opens{" "}
                  {new Date(
                    match.availabilityOpensAt + "T00:00:00",
                  ).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </Text>
                {canSelect && (
                  <Pressable
                    onPress={async () => {
                      if (!club || !id) return;
                      const today = new Date().toISOString().slice(0, 10);
                      await updateDoc(
                        doc(db, "clubs", club.id, "matches", id),
                        { availabilityOpensAt: today },
                      );
                    }}
                    style={({ pressed }) => ({
                      marginTop: 12,
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <View
                      style={{
                        backgroundColor: colors.brand,
                        padding: 12,
                        borderRadius: radius.md,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        style={{
                          color: "#fff",
                          fontWeight: "700",
                          fontSize: 14,
                        }}
                      >
                        Open availability now
                      </Text>
                    </View>
                  </Pressable>
                )}
              </View>
            </Section>
          )}

        {/* My availability — collapses to a pill once user has responded */}
        {(!match?.availabilityOpensAt ||
          match.availabilityOpensAt <= new Date().toISOString().slice(0, 10)) &&
          (!isTeamSelected || membership?.role === "admin") && (
            <Section>
              <SectionLabel>Your availability</SectionLabel>

              {myStatus?.status && !availExpanded ? (
                <Pressable
                  onPress={() => setAvailExpanded(true)}
                  style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 14,
                      borderRadius: radius.md,
                      borderWidth: 1.5,
                      borderColor: statusConfig[myStatus.status].border,
                      backgroundColor: statusConfig[myStatus.status].bg,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Text style={{ fontSize: 16 }}>
                        {statusConfig[myStatus.status].emoji}
                      </Text>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: "700",
                          color: statusConfig[myStatus.status].text,
                        }}
                      >
                        You're{" "}
                        {statusConfig[myStatus.status].label.toLowerCase()}
                      </Text>
                    </View>
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: "600",
                        color: statusConfig[myStatus.status].text,
                      }}
                    >
                      Change
                    </Text>
                  </View>
                </Pressable>
              ) : (
                <View style={{ flexDirection: "row", gap: 8 }}>
                  {(
                    [
                      "available",
                      "unavailable",
                      "maybe",
                    ] as AvailabilityStatus[]
                  ).map((status) => {
                    const c = statusConfig[status];
                    const active = myStatus?.status === status;
                    return (
                      <Pressable
                        key={status}
                        onPress={async () => {
                          await handleSetAvailability(status);
                          setAvailExpanded(false);
                        }}
                        disabled={saving}
                        style={({ pressed }) => ({
                          flex: 1,
                          paddingVertical: 12,
                          borderRadius: radius.md,
                          alignItems: "center",
                          backgroundColor: active ? c.bg : colors.bg,
                          borderWidth: 1.5,
                          borderColor: active ? c.border : colors.line,
                          opacity: pressed ? 0.7 : saving ? 0.5 : 1,
                        })}
                      >
                        <Text
                          style={{
                            fontSize: 13,
                            fontWeight: "700",
                            color: active ? c.text : colors.inkMuted,
                          }}
                        >
                          {c.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </Section>
          )}

        {/* Available */}
        <Section>
          <SectionLabel>Available ({available.length})</SectionLabel>
          {available.length > 0 ? (
            <ResponseCard>
              {available.map((r, i) => (
                <ResponseRow key={r.id} hasBorder={i < available.length - 1}>
                  <ResponseAvatar bg={colors.brand50}>
                    <ResponseAvatarText color={colors.brandDark}>
                      {r.displayName?.[0]?.toUpperCase()}
                    </ResponseAvatarText>
                  </ResponseAvatar>
                  <Flex1>
                    <ResponseName>
                      {r.displayName}
                      {r.driving ? " 🚗" : ""}
                    </ResponseName>
                    {r.note ? <ResponseNote>{r.note}</ResponseNote> : null}
                  </Flex1>
                  <StatusEmoji color={colors.brandDark}>{"\u2705"}</StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          ) : (
            <EmptySection>No one yet</EmptySection>
          )}
        </Section>

        {/* Maybe */}
        {maybe.length > 0 && (
          <Section>
            <SectionLabel>Maybe ({maybe.length})</SectionLabel>
            <ResponseCard>
              {maybe.map((r, i) => (
                <ResponseRow key={r.id} hasBorder={i < maybe.length - 1}>
                  <ResponseAvatar bg={colors.amber50}>
                    <ResponseAvatarText color={colors.amber}>
                      {r.displayName?.[0]?.toUpperCase()}
                    </ResponseAvatarText>
                  </ResponseAvatar>
                  <Flex1>
                    <ResponseName>{r.displayName}</ResponseName>
                    {r.note ? <ResponseNote>{r.note}</ResponseNote> : null}
                  </Flex1>
                  <StatusEmoji color={colors.amber}>
                    {"\ud83e\udd14"}
                  </StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          </Section>
        )}

        {/* Not responded */}
        {notResponded.length > 0 && (
          <Section>
            <SectionLabel>Not responded ({notResponded.length})</SectionLabel>
            <ResponseCard>
              {notResponded.map((m, i) => (
                <ResponseRow key={m.id} hasBorder={i < notResponded.length - 1}>
                  <ResponseAvatar bg={colors.bg}>
                    <ResponseAvatarText color={colors.inkMuted}>
                      {m.displayName?.[0]?.toUpperCase()}
                    </ResponseAvatarText>
                  </ResponseAvatar>
                  <Flex1>
                    <ResponseName style={{ color: colors.inkMuted }}>
                      {m.displayName}
                    </ResponseName>
                  </Flex1>
                  <StatusEmoji color={colors.inkMuted}>—</StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          </Section>
        )}

        {/* Unavailable */}
        {unavailable.length > 0 && (
          <Section>
            <SectionLabel>Unavailable ({unavailable.length})</SectionLabel>
            <ResponseCard>
              {unavailable.map((r, i) => (
                <ResponseRow key={r.id} hasBorder={i < unavailable.length - 1}>
                  <ResponseAvatar bg={colors.dangerLight}>
                    <ResponseAvatarText color={colors.danger}>
                      {r.displayName?.[0]?.toUpperCase()}
                    </ResponseAvatarText>
                  </ResponseAvatar>
                  <Flex1>
                    <ResponseName>{r.displayName}</ResponseName>
                    {r.note ? <ResponseNote>{r.note}</ResponseNote> : null}
                  </Flex1>
                  <StatusEmoji color={colors.danger}>{"\u274C"}</StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          </Section>
        )}

        <BottomSpacer />
      </ContentWidth>

      {/* Withdrawal reason bottom sheet */}
      <Modal visible={withdrawSheetVisible} animationType="slide" transparent>
        <ModalContent
          onClose={() => {
            setWithdrawSheetVisible(false);
            setSelectedReason(null);
          }}
        >
          <WithdrawSheet>
            <WithdrawSheetTitle>Withdraw from match</WithdrawSheetTitle>
            <WithdrawSheetMessage>
              Please select a reason for withdrawing from {match.team} vs{" "}
              {match.opponent}.
            </WithdrawSheetMessage>

            <ReasonPillsRow>
              {WITHDRAWAL_REASONS.map((reason) => (
                <Pressable
                  key={reason}
                  onPress={() => setSelectedReason(reason)}
                  style={({ pressed }) => ({ opacity: pressed ? 0.8 : 1 })}
                >
                  <ReasonPill isActive={selectedReason === reason}>
                    <ReasonPillText isActive={selectedReason === reason}>
                      {reason}
                    </ReasonPillText>
                  </ReasonPill>
                </Pressable>
              ))}
            </ReasonPillsRow>

            <Pressable
              onPress={handleWithdraw}
              disabled={!selectedReason || withdrawing}
              style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
            >
              <WithdrawConfirmBtn disabled={!selectedReason || withdrawing}>
                {withdrawing ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <WithdrawConfirmBtnText>
                    Confirm Withdrawal
                  </WithdrawConfirmBtnText>
                )}
              </WithdrawConfirmBtn>
            </Pressable>

            <Pressable
              onPress={() => {
                setWithdrawSheetVisible(false);
                setSelectedReason(null);
              }}
              style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
            >
              <WithdrawCancelBtn>
                <WithdrawCancelBtnText>Cancel</WithdrawCancelBtnText>
              </WithdrawCancelBtn>
            </Pressable>
          </WithdrawSheet>
        </ModalContent>
      </Modal>

      <AlertSheet {...alertProps} />
      <ConfirmSheet {...confirmProps} />
    </KeyboardAwareScroll>
  );
}
